<template>
  <Transition name="sidebar">
    <aside
      v-if="offcanvas.isOpen"
      class="sidebar"
      :class="{ 'sidebar-open': offcanvas.isOpen }"
    >
      <div class="sidebar-header">

        <button
          type="button"
          class="sidebar-close-btn"
          @click="closeSidebar"
          aria-label="Cerrar menú"
        >
          <i class="fas fa-times"/>
        </button>
      </div>

      <div class="sidebar-user">
        <div class="user-avatar">
          <div class="avatar-placeholder">
            <i class="fas fa-user"/>
          </div>
        </div>
        <div class="user-info">
          <h6 class="user-name">{{ userName }}</h6>
          <span class="user-role">{{ userRole }}</span>
        </div>
      </div>

      <nav class="sidebar-nav">
        <ul class="nav-list">
          <li
            v-for="item in menuItems"
            :key="item.id"
            class="nav-item"
          >
            <router-link
              v-if="!item.children"
              :to="{ name: item.route }"
              class="nav-link"
              :class="{ 'active': isActiveRoute(item.route) }"
              @click="closeSidebar"
            >
              <i
                :class="item.icon ? item.icon : 'fas fa-circle nav-icon'"
                class="nav-icon"/>
              <span class="nav-text">{{ item.label }}</span>
            </router-link>

            <div
              v-else
              class="nav-item-with-children">
              <button
                class="nav-link nav-toggle"
                :class="{ 'active': isActiveChildRoute(item.children) }"
                @click="toggleSubmenu(item.id)"
              >
                <i
                  :class="item.icon"
                  class="nav-icon"/>
                <span class="nav-text">{{ item.label }}</span>
                <i 
                  class="fas fa-chevron-down nav-arrow"
                  :class="{ 'rotated': openSubmenus.includes(item.id) }"
                />
              </button>
              
              <Transition name="submenu">
                <ul
                  v-if="openSubmenus.includes(item.id)"
                  class="submenu"
                >
                  <li
                    v-for="child in item.children"
                    :key="child.id"
                    class="submenu-item"
                  >
                    <router-link
                      :to="{ name: child.route }"
                      class="submenu-link"
                      :class="{ 'active': isActiveRoute(child.route) }"
                      @click="closeSidebar"
                    >
                      <i
                        :class="child.icon"
                        class="submenu-icon"/>
                      <span class="submenu-text">{{ child.label }}</span>
                    </router-link>
                  </li>
                </ul>
              </Transition>
            </div>
          </li>
        </ul>
      </nav>

      <div class="sidebar-footer">
        <button
          class="config-btn"
          @click="handlePasswordChange"
        >
          <i class="fa-solid fa-gears"/>
          <span>Cambiar contraseña</span>
        </button>
        <button
          class="logout-btn"
          @click="handleLogout"
        >
          <i class="fas fa-sign-out-alt"/>
          <span>Cerrar Sesión</span>
        </button>
      </div>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useOffcanvasStore } from '@/stores/offcanvas.ts';
import { useAuthStore } from '@/stores/useAuthStore.ts';
import { ROUTE_NAMES } from '@/shared/constants/routeNames.ts';
import {buildMenuFromApi} from "@/shared/utils/menuBuilder.ts";

const offcanvas = useOffcanvasStore();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

const openSubmenus = ref<number[]>([]);

const userName = computed(() => {
  return authStore.user?.user_data?.NOMBRE_U || 'Usuario';
});

const userRole = computed(() => {
  return authStore.user?.user_rol || 'NO ROL';
});

const menuItems = computed(() => {
  const home = {
    id: 100,
    label: 'Home',
    icon: 'fa-solid fa-house',
    route: ROUTE_NAMES.WELCOME.HOME.name,
    children: null
  }
  const menu =  buildMenuFromApi(authStore.user.user_data?.permisos)

  return [home, ...menu]

});

const closeSidebar = () => {
  offcanvas.close();
};

const toggleSubmenu = (itemId: number) => {
  const index = openSubmenus.value.indexOf(itemId);
  if (index > -1) {
    openSubmenus.value.splice(index, 1);
  } else {
    openSubmenus.value.push(itemId);
  }
};

const isActiveRoute = (routeName: string | null) => {
  if (!routeName) return false;
  return route.name === routeName;
};

const isActiveChildRoute = (children: any[]) => {
  if (!children) return false;
  return children.some(child => route.name === child.route);
};

const handleLogout = async () => {
  closeSidebar();
  authStore.logout();
  await router.push({ name: ROUTE_NAMES.LOGIN.name });
};

const handlePasswordChange = async () => {
  closeSidebar()
  await router.push({name: ROUTE_NAMES.CHANGE_PASSWORD.name})
}

watch(() => route.path, () => {
  if (offcanvas.isOpen) {
    closeSidebar();
  }
});
</script>

<style lang="scss" scoped>
@use '/src/assets/scss/custom/variables' as *;
$sidebar-width: 280px;
$sidebar-bg: #2c3e50;
$sidebar-text: #ecf0f1;
$sidebar-hover: #34495e;
$sidebar-active: #3498db;
$sidebar-border: #34495e;
$transition-duration: 0.3s;

.nav-toggle.active,
.nav-toggle:focus,
.nav-item-with-children.open > .nav-link {
  background: $primary-action-color-lighten;
  color: $text-dark-color;
}


.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  backdrop-filter: blur(2px);
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: $sidebar-width;
  height: 100vh;
  background: $primary-structure-color;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  @media (max-width: 768px) {
    width: 100%;
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: end;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sidebar-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.sidebar-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: $sidebar-text;
}

.sidebar-close-btn {
  background: none;
  border: none;
  color: $sidebar-text;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
}

// Información del usuario
.sidebar-user {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid $sidebar-border;
  background: rgba(255, 255, 255, 0.02);
}

.user-avatar {
  flex-shrink: 0;
}

.avatar-img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: $sidebar-text;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: $sidebar-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  display: block;
  margin-top: 0.25rem;
}

// Navegación
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  color: $sidebar-text;
  text-decoration: none;
  transition: all 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: $primary-action-color-lighten;
    color: $text-dark-color;
    text-decoration: none;
  }
  &.active {
    background: $primary-action-color;
    color: $text-light-color;
    font-weight: 600;
  }
}

.nav-icon {
  width: 20px;
  text-align: center;
  font-size: 1rem;
}

.nav-text {
  flex: 1;
  font-size: 0.95rem;
}

.nav-arrow {
  transition: transform 0.2s ease;
  
  &.rotated {
    transform: rotate(180deg);
  }
}

// Submenús
.submenu {
  list-style: none;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.submenu-item {
  margin: 0;
}

.submenu-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem 0.75rem 3.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: $sidebar-text;
    text-decoration: none;
  }

  &.active {
    background: rgba(52, 152, 219, 0.3);
    color: $sidebar-active;
    font-weight: 600;
  }
}

.submenu-icon {
  width: 16px;
  text-align: center;
  font-size: 0.875rem;
}

.submenu-text {
  flex: 1;
}

// Footer
.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid $sidebar-border;
  background: rgba(0, 0, 0, 0.2);
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: $secondary-action-color;
  border: none;
  border-radius: 6px;
  color: $text-light-color;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $secondary-action-color-lighten;
    transform: translateY(-1px);
    color: $text-dark-color;
  }
}

.config-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: $primary-action-color-opacity;
  border: none;
  border-radius: 6px;
  color: $text-light-color;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $primary-action-color-lighten;
    transform: translateY(-1px);
    color: $text-dark-color;
  }
}

// Transiciones
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity $transition-duration ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.sidebar-enter-active,
.sidebar-leave-active {
  transition: transform $transition-duration ease;
}

.sidebar-enter-from,
.sidebar-leave-to {
  transform: translateX(-100%);
}

.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s ease;
}

.submenu-enter-from,
.submenu-leave-to {
  opacity: 0;
  max-height: 0;
}

.submenu-enter-to,
.submenu-leave-from {
  opacity: 1;
  max-height: 200px;
}
</style>
